import { useRecoilValue } from 'recoil';
import ResizePanel from 'react-resize-panel-ts';
import * as K from 'common/constants';
import { PlotModel } from 'common/types';
import { Cell, Column, PlotData } from 'common/graphqlTypes';
import { canBeRenderedAsPieChart, getPQNumericValue } from 'common/util/pqUtil';
import { getPieSeriesData } from 'common/util/graph';
import { DashboardPlotLabelValue, getSeriesData } from 'components/dashboards/dashboardData';
import { queryTimeRangeState, searchParamState } from 'components/common/Managers/RouteStateManager.state';
import NoEventsFound from 'components/search/PowerQueriesPage/components/NoEventsFound/NoEventsFound';
import TimelineLegend from 'components/common/Legends/TimelineLegend';
import PowerQueryPieChart from 'components/search/PowerQueriesPage/components/PowerQueryCharts/PowerQueryPieChart';
import PowerQueryTimeSeriesChart from 'components/search/PowerQueriesPage/components/PowerQueryCharts/PowerQueryTimeSeriesChart';
import PowerQueryChartLegend from 'components/search/PowerQueriesPage/components/PowerQueryCharts/PowerQueryChartLegend';
import { plotsStylesState } from 'styles/stylesProvider';

import './PowerQueryChartResults.scss';

export interface PowerQueryChartResultsProps {
  columns: Column[];
  cells: Cell[][];
  plotResult?: PlotData;
}

// Helper function for dashboard/PQ pie chart consistency.
// TODO: look into 'smarter' ways to decide which is the right column to use as the pie slices.
export const getPieChartValueColumn = (columns: Column[]) => (columns?.length > 1 ? 1 : 0);

/**
 * Power Query result area. In charge of rendering all chart types as well as the legend component.
 * Handles the state management communication between chart and legend and URL params.
 */
const PowerQueryChartResults = ({ columns, cells, plotResult }: PowerQueryChartResultsProps): JSX.Element => {
  const graphStyleParam = useRecoilValue(searchParamState(K.URL_PARAM.GRAPH_STYLE));
  const pieStyleParam = useRecoilValue(searchParamState(K.URL_PARAM.PIE_STYLE));
  const slicesParam = useRecoilValue(searchParamState(K.URL_PARAM.MAX_PIE_SLICES));
  const yScaleParam = useRecoilValue(searchParamState(K.URL_PARAM.Y_SCALE));
  const lineSmoothingParam = useRecoilValue(searchParamState(K.URL_PARAM.LINE_SMOOTHING));
  const hideGraphsParam = useRecoilValue(searchParamState(K.URL_PARAM.HIDE_GRAPHS));
  const plotsStyles = useRecoilValue(plotsStylesState);
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;

  const hideGraphsList = hideGraphsParam ? JSON.parse(hideGraphsParam) : [];
  const timeRange = { startTime: parseFloat(startTime), endTime: parseFloat(endTime) };
  let chartContent;
  let plotGroups: Record<string, PlotModel[]> = {
    plots: [],
  };
  if (graphStyleParam === K.PQ_DATA_VIS_TYPES.PIE) {
    // TODO: This limits pie charts to only work for single column numeric tables or tables where the second column is numeric.
    // We should probably adjust this to try to render something as long as the last column is numeric. Dashboards doesn't do it
    // this way, so that is why we are taking this approach for now for consistency.
    const valueCol = getPieChartValueColumn(columns);
    // Gather the data and calc totals and percentages for the pie to ensure current query is valid
    const valuesColumn = cells.map((rowValue) => getPQNumericValue(rowValue[valueCol]));
    if (!canBeRenderedAsPieChart(valuesColumn)) {
      return (
        <NoEventsFound
          message={
            'Please ensure query is structured correctly -- One or two columns, where the last column is numeric.'
          }
        />
      );
    }
    const seriesData = getPieSeriesData(
      [...cells],
      valueCol,
      0,
      slicesParam ? Number(slicesParam) : K.DEFAULT_MAX_PIE_SLICES,
    );
    const mappedSeriesData: Array<DashboardPlotLabelValue> = [];
    seriesData.forEach((series, idx) => {
      const visible = !hideGraphsList.includes(series.name);
      const color = plotsStyles[idx % plotsStyles.length];
      plotGroups.plots.push({
        name: series.name,
        op: series.name,
        pieData: series.y,
        data: [],
        visible,
        color,
      });
      mappedSeriesData.push({
        ...series,
        visible,
        color,
      });
    });
    chartContent = (
      <>
        <TimelineLegend title="Pie Chart" subtitle={columns[valueCol].name} timeRange={timeRange} />
        <PowerQueryPieChart
          seriesData={mappedSeriesData}
          tooltipLabel={columns[valueCol].name}
          pieStyle={pieStyleParam ? pieStyleParam : K.PIE_CHART_TYPES.PIE}
        />
      </>
    );
  } else if (plotResult) {
    plotResult?.plots?.forEach((plot, idx) => {
      const mappedSeries = getSeriesData(
        plotResult.xAxis,
        plot.samples,
        graphStyleParam === K.CHART_TYPES.STACKED_BAR,
        plotResult.sliceInfo,
      );
      plotGroups.plots.push({
        name: plot.label,
        data: mappedSeries.seriesData,
        visible: !hideGraphsList.includes(plot.label),
        color: plotsStyles[idx % plotsStyles.length],
        op: plot.label,
        maxValue: mappedSeries.maxValue,
      });
    });
    const title = graphStyleParam === K.CHART_TYPES.STACKED_BAR ? 'Bar/Column Chart' : 'Line Chart';
    chartContent = (
      <>
        <TimelineLegend title={title} timeRange={timeRange} />
        <PowerQueryTimeSeriesChart
          plotData={plotGroups}
          graphStyle={graphStyleParam}
          lineSmoothing={lineSmoothingParam}
          yScale={yScaleParam ? yScaleParam : K.Y_SCALE_OPTIONS.LINEAR}
        />
      </>
    );
  }

  return (
    <div className="PowerQueryChartResults">
      <div className="PowerQueryChartResults__ChartContainer">{chartContent}</div>
      <ResizePanel direction="w" handleClass="PowerQueryChartResults__ResizePanelHandle">
        <PowerQueryChartLegend plotGroups={plotGroups} timeStamps={plotResult?.xAxis} />
      </ResizePanel>
    </div>
  );
};

export default PowerQueryChartResults;
